import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValidSaudiPhoneNumber } from "../../../../setup/util/phoneNumber.helpers";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import PartyTable from "../partyTable";
import MilestoneHeader from "../milestoneHeader";
import styles from "./styles.module.scss";
import OutlinedButton from "../../../../components/common/OutlinedButton";
import MilestoneFormNavButton from "../milestoneFormNavButton";
import AddPartyModal from "../addPartyModal";
import { useFieldArray, useFormContext } from "react-hook-form";
import BulkUploadModal, { PartyErrorsMap } from "../bulkUploadModal";
import ContractAlert from "../alert";
import { ESenderRole } from "../../../../setup/api/services/contract/models";
import { AuthConfig } from "../../../../setup/context/config/authConfig";
import { getOrganization } from "../../../../setup/api/services/organization";
import { useOrganizationCode } from "../../../../setup/util/extractOrganizationCode";
import { IOrganizations } from "../../../../setup/types/Organisation";

interface IStepFourProps {
  currentStep: number;
  handleNext: () => void;
  handlePrev: () => void;
  handleSaveDraft: () => void;
  loading: boolean;
}

const StepFour = ({
  currentStep,
  handleNext,
  handlePrev,
  handleSaveDraft,
  loading,
}: IStepFourProps) => {
  const { watch, control, setValue } = useFormContext();
  const { t } = useTranslation();
  const userToken = AuthConfig.getToken();

  const {
    fields: parties,
    append: addParty,
    remove: removeParty,
    update: updateParty,
  } = useFieldArray({
    control,
    name: `milestones.${watch("currentMilestoneIndex") || 0}.parties`,
    rules: { minLength: 2 },
  });

  const organizationCode = useOrganizationCode(userToken);
  const [organization, setOrganization] = useState<IOrganizations | undefined>(
    undefined
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [partyIndex, setPartyIndex] = useState(0);
  const [mode, setMode] = useState("add");
  const [partyErrors, setPartyErrors] = useState<PartyErrorsMap>({});
  const currentMilestoneIndex = watch("currentMilestoneIndex") || 0;
  const buyerOnly = !!watch(`milestones.${currentMilestoneIndex}.buyerOnly`);

  // Helper functions for consistent validation with bulkUploadModal
  const checkInternalDuplicates = (parties: any[]) => {
    const phoneNumbers = new Map<string, any[]>();
    parties.forEach((party) => {
      if (!party.phoneNumber) return;
      if (!phoneNumbers.has(party.phoneNumber)) {
        phoneNumbers.set(party.phoneNumber, []);
      }
      phoneNumbers.get(party.phoneNumber)?.push(party);
    });
    return phoneNumbers;
  };

  // Validate a single party and return any errors
  const validateParty = (party, allParties) => {
    const errors: Record<string, string> = {};

    // Check name field
    if (!party.name || party.name.length < 5) {
      errors.name = t("err_min_chars", { min: 5 });
    }

    // Check phoneNumber field
    if (party.role !== ESenderRole?.BROKER) {
      if (!isValidSaudiPhoneNumber(party.phoneNumber)) {
        errors.phoneNumber = t("err_invalid_phone");
      } else {
        const phoneNumbersMap = checkInternalDuplicates(allParties);
        const duplicates = phoneNumbersMap.get(party.phoneNumber);
        if (duplicates && duplicates.length > 1) {
          errors.phoneNumber = t("err_duplicate_phone");
        }
      }
    }

    // Check amount field based on party type
    if (party.amount < 0 && party.role !== ESenderRole?.BROKER) {
      errors.amount = t("err_number_min", { min: 0 });
    }

    // Check role field for providers
    if (party.type === "provider" && !party.role) {
      errors.role = t("err_role_required_for_providers");
    }

    return Object.keys(errors).length ? errors : null;
  };

  // Track which party IDs have already been validated on init so late-arriving
  // draft parties (loaded after the broker is auto-added) still get validated.
  const validatedPartyIdsRef = useRef<Set<string>>(new Set());
  const lastMilestoneIndexRef = useRef<number>(currentMilestoneIndex);

  useEffect(() => {
    // Reset when the milestone changes so the new milestone's parties are validated
    if (lastMilestoneIndexRef.current !== currentMilestoneIndex) {
      validatedPartyIdsRef.current = new Set();
      lastMilestoneIndexRef.current = currentMilestoneIndex;
    }

    const currentParties = watch(`milestones.${currentMilestoneIndex}.parties`);
    if (!currentParties?.length) return;

    const newErrors: PartyErrorsMap = {};
    let hasErrors = false;

    currentParties.forEach((party) => {
      const errorKey = party.rowId || party.id;
      if (validatedPartyIdsRef.current.has(errorKey)) return;

      const partyErrors = validateParty(party, currentParties);
      if (partyErrors) {
        newErrors[errorKey] = partyErrors;
        hasErrors = true;
      }
      validatedPartyIdsRef.current.add(errorKey);
    });

    if (hasErrors) {
      setPartyErrors((prev) => ({ ...prev, ...newErrors }));
    }
  }, [watch(`milestones.${currentMilestoneIndex}.parties`)]);

  // Helper function to check all duplicates and update the errors map
  const validateAllParties = (currentParties) => {
    if (!currentParties || currentParties.length === 0) return;

    // First, identify all duplicates
    const duplicatesMap = checkInternalDuplicates(currentParties);
    const duplicatePhoneGroups: Record<string, any[]> = {};

    // Group parties by phone number where there are duplicates
    duplicatesMap.forEach((parties, phoneNumber) => {
      if (parties.length > 1) {
        // Sort parties to ensure consistent ordering (keep newest last)
        const sortedParties = [...parties].sort((a, b) => {
          // Sort by id to ensure consistent ordering - earliest first
          const idA = a.id || "";
          const idB = b.id || "";
          return idA.localeCompare(idB);
        });
        duplicatePhoneGroups[phoneNumber] = sortedParties;
      }
    });

    // Update errors for each party
    const updatedErrors = { ...partyErrors };
    let hasChanges = false;

    // Clear previous duplicate errors from all parties
    currentParties.forEach((party) => {
      const errorKey = party.rowId || party.id;
      if (updatedErrors[errorKey]?.phoneNumber === t("err_duplicate_phone")) {
        if (Object.keys(updatedErrors[errorKey]).length === 1) {
          delete updatedErrors[errorKey];
        } else {
          const currentErrors = { ...updatedErrors[errorKey] };
          delete currentErrors.phoneNumber;
          updatedErrors[errorKey] = currentErrors;
        }
        hasChanges = true;
      }
    });

    // Now mark only the second party in each duplicate group with an error
    Object.values(duplicatePhoneGroups).forEach((parties) => {
      if (parties.length > 1) {
        // Get all parties except the first one
        const duplicateParties = parties.slice(1);

        // Mark each duplicate party with an error
        duplicateParties.forEach((party) => {
          const errorKey = party.rowId || party.id;
          const currentErrors = updatedErrors[errorKey] || {};

          if (
            !currentErrors.phoneNumber ||
            currentErrors.phoneNumber !== t("err_duplicate_phone")
          ) {
            currentErrors.phoneNumber = t("err_duplicate_phone");
            updatedErrors[errorKey] = currentErrors;
            hasChanges = true;
          }
        });
      }
    });

    // Update state if anything changed
    if (hasChanges) {
      setPartyErrors(updatedErrors);
    }
  };

  const fetchOrganization = useCallback(async () => {
    if (!organizationCode) return;
    const response = await getOrganization(
      organizationCode as unknown as string
    );
    setOrganization(response.data);
  }, [organizationCode]);

  useEffect(() => {
    if (organizationCode) {
      fetchOrganization();
    }
  }, [fetchOrganization, organizationCode]);

  useEffect(() => {
    const currentParties = watch(`milestones.${currentMilestoneIndex}.parties`);

    if (Object.keys(partyErrors).length > 0) {
      // Build a lookup of current party keys (rowId || id)
      const currentPartyKeys = new Map();
      currentParties?.forEach((party) => {
        const key = party.rowId || party.id;
        currentPartyKeys.set(key, party);
      });

      const updatedErrors: PartyErrorsMap = { ...partyErrors };
      let hasChanges = false;

      // Remove errors for parties that no longer exist
      for (const key of Object.keys(updatedErrors)) {
        if (!currentPartyKeys.has(key)) {
          delete updatedErrors[key];
          hasChanges = true;
        }
      }

      // Revalidate existing parties
      currentParties?.forEach((party) => {
        const key = party.rowId || party.id;
        if (updatedErrors[key]) {
          // ✅ Name field
          if (updatedErrors[key].name && party.name?.length >= 5) {
            delete updatedErrors[key].name;
            hasChanges = true;
          }

          // ✅ Phone number field - handle invalid phone number case
          if (
            updatedErrors[key].phoneNumber === t("err_invalid_phone") &&
            (isValidSaudiPhoneNumber(party.phoneNumber) ||
              party.role === ESenderRole?.BROKER)
          ) {
            delete updatedErrors[key].phoneNumber;
            hasChanges = true;
          }

          // Note: duplicate phone errors are handled separately in validateAllParties

          // ✅ Amount field
          if (
            (updatedErrors[key].amount &&
              party.amount >= 1 &&
              party.role !== ESenderRole?.BROKER) ||
            (party?.amount >= 0 &&
              updatedErrors[key].amount &&
              party.role === ESenderRole?.BROKER)
          ) {
            delete updatedErrors[key].amount;
            hasChanges = true;
          }

          // ✅ Role field (for providers)
          if (updatedErrors[key].role && party.role) {
            delete updatedErrors[key].role;
            hasChanges = true;
          }

          // ✅ Clean up empty error objects
          if (Object.keys(updatedErrors[key]).length === 0) {
            delete updatedErrors[key];
            hasChanges = true;
          }
        }
      });

      // Check for duplicates after handling other errors
      validateAllParties(currentParties);

      // ✅ Update error state if anything changed
      if (hasChanges) {
        setPartyErrors(
          Object.keys(updatedErrors).length === 0 ? {} : updatedErrors
        );
      }
    }
  }, [currentMilestoneIndex, partyErrors, watch, parties]);

  // Whenever parties change, revalidate duplicates
  useEffect(() => {
    const currentParties = watch(`milestones.${currentMilestoneIndex}.parties`);
    if (currentParties?.length > 0) {
      validateAllParties(currentParties);
    }
  }, [watch(`milestones.${currentMilestoneIndex}.parties`)]);

  const allParties = watch(`milestones.${currentMilestoneIndex}.parties`);
  const isValid = useMemo(() => {
    if (buyerOnly) return true;

    const customerParties = allParties?.filter(
      (party) => party?.type === "customer"
    );
    const providerParties = allParties?.filter(
      (party) => party?.type === "provider"
    );

    const customerSum = customerParties?.reduce(
      (acc, curr) => acc + curr?.amount,
      0
    );
    const providerSum = providerParties?.reduce(
      (acc, curr) => acc + curr?.amount,
      0
    );

    return (
      parties?.length >= 1 &&
      customerParties?.length >= 1 &&
      providerParties?.length >= 1 &&
      Number(customerSum).toFixed(2) === Number(providerSum).toFixed(2) &&
      Object.keys(partyErrors).filter((key) => key.startsWith("provider"))
        .length === 0
    );
  }, [allParties, parties?.length, partyErrors, buyerOnly]);

  useEffect(() => {
    if (!organization) return;
    if (buyerOnly) return;
    const brokerAlreadyExists = allParties?.some(
      (party) =>
        party?.role === ESenderRole?.BROKER ||
        party?.phoneNumber === String(organization?.accountUserIdentifier)
    );

    if (!brokerAlreadyExists) {
      const uId = Math.random().toString(36).substring(2, 9);
      addParty({
        id: `broker-${Date.now()}-${uId}`,
        rowId: `broker-${Date.now()}-${uId}`,
        phoneNumber: String(organization?.accountUserIdentifier ?? ""),
        name: organization?.name,
        amount: 0,
        type: "provider",
        role: ESenderRole?.BROKER,
        arbitrator: true,
      });
    }
  }, [organization, allParties, addParty, buyerOnly]);

  // Defensive dedup: remove duplicate providers that can accumulate from draft load + broker init
  useEffect(() => {
    if (!allParties?.length) return;
    const seenKeys = new Set<string>();
    let hasDups = false;
    let brokerSeen = false;
    const deduped = allParties.filter((party: any) => {
      if (party?.role === ESenderRole.BROKER) {
        if (brokerSeen) {
          hasDups = true;
          return false;
        }
        brokerSeen = true;
        return true;
      }
      const identity = String(party?.phoneNumber || "").replaceAll(" ", "");
      if (!identity) return true;
      const key = `${party.type ?? ""}:${identity}`;
      if (seenKeys.has(key)) {
        hasDups = true;
        return false;
      }
      seenKeys.add(key);
      return true;
    });
    if (hasDups) {
      setValue(`milestones.${currentMilestoneIndex}.parties`, deduped);
    }
  }, [allParties]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.stepFour}>
      <MilestoneHeader
        complexContractTitle={watch("title")}
        currentStep={currentStep}
        buyerOnly={buyerOnly}
      />
      {!isValid &&
        !buyerOnly &&
        allParties?.filter((party) => party?.type === "provider")?.length >=
          1 && (
          <ContractAlert
            variant="error"
            title={t("lbl_parties_total_mismatch")}
          />
        )}
      <div className={styles.actionsRow}>
        <p>
          {`${t("lbl_customers_have_paid")}`}
          <span style={{ color: "black" }}>
            {allParties
              ?.filter((party) => party?.type === "customer")
              ?.reduce((acc, curr) => acc + curr?.amount, 0)
              ?.toLocaleString()}{" "}
            {t("lbl_saudi_currency")}
          </span>
        </p>
        <div className={styles.uploadButton}>
          <OutlinedButton
            onClick={() => {
              setMode("add");
              setUploadModalOpen(true);
            }}>
            <img
              src="/assets/images/svg/uploadFile.svg"
              alt="upload"
              className={styles.uploadIcon}
            />
            <p>{t("lbl_upload_csv_file")}</p>
          </OutlinedButton>
        </div>
        <div className={styles.addButton}>
          <PrimaryButton
            onClick={() => {
              const parties = watch(
                `milestones.${currentMilestoneIndex}.parties`
              );
              setMode("add");
              setPartyIndex(parties?.length);
              setModalOpen(true);
            }}>
            {t("lbl_add_party_with_plus")}
          </PrimaryButton>
        </div>
        {selectedIndices?.length > 0 && (
          <div className={styles.addButton}>
            <PrimaryButton
              onClick={() => {
                removeParty(selectedIndices);
                setSelectedIndices([]);
              }}>
              {t("lbl_remove_all_parties")}
            </PrimaryButton>
          </div>
        )}
      </div>
      <PartyTable
        resetSelectionTrigger={selectedIndices?.length === 0 ? 1 : 0}
        enableBulkSelect
        onSelectionChange={(selectedIndices) =>
          setSelectedIndices([...selectedIndices])
        }
        rows={
          watch(`milestones.${currentMilestoneIndex}.parties`)?.filter(
            (party) => party?.type === "provider"
          ) || []
        }
        columns={{
          showActions: true,
          showStatus: false,
          showRole: true,
          showFee: false,
          showTotal: false,
        }}
        getIndexByRow={(row) =>
          (
            watch(`milestones.${currentMilestoneIndex}.parties`) || []
          )?.findIndex((p) => p?.rowId === row?.rowId)
        }
        handleEdit={(index) => {
          setMode("edit");
          setPartyIndex(index);
          setModalOpen(true);
        }}
        handleDelete={(index) => {
          removeParty(index);
          // The validation will happen automatically through useEffect when parties change
        }}
        className={styles.partyTable}
        partyErrors={Object.fromEntries(
          Object.entries(partyErrors).filter(([key]) =>
            key.startsWith(`provider-`)
          )
        )}
      />
      <p className={styles.countLabel}>
        {t("lbl_parties_count_label")}{" "}
        {
          watch(`milestones.${currentMilestoneIndex}.parties`)?.filter(
            (party) => party?.type === "provider"
          )?.length
        }
      </p>
      <MilestoneFormNavButton
        disabled={!isValid || loading}
        handleNext={handleNext}
        handlePrev={handlePrev}
        handleSaveDraft={handleSaveDraft}
      />
      <AddPartyModal
        mode={mode}
        modalOpen={modalOpen}
        handleClose={() => {
          setModalOpen(false);
        }}
        handleSave={(partyData) => {
          const parties = watch(`milestones.${currentMilestoneIndex}.parties`);
          const rowPartyId = partyData?.rowId || "";

          // Check if this is an edit of an existing party
          if (parties?.some((party) => party?.rowId === rowPartyId)) {
            updateParty(
              parties?.findIndex((party) => party?.rowId === rowPartyId),
              { ...partyData }
            );
          } else {
            // Add new party
            const uId = Math.random().toString(36).substring(2, 9);
            addParty({
              ...partyData,
              id: `provider-${partyIndex}-${Date.now()}-${uId}`,
              type: currentStep === 2 ? "customer" : "provider",
            });
          }

          setModalOpen(false);
        }}
        type={currentStep === 2 ? "customer" : "provider"}
        partyIndex={partyIndex}
      />
      <BulkUploadModal
        type={currentStep === 2 ? "customer" : "provider"}
        modalOpen={uploadModalOpen}
        handleClose={() => {
          setUploadModalOpen(false);
        }}
        handleSave={(partyData, errors = {}) => {
          setUploadModalOpen(false);

          // Add parties in bulk
          if (Array.isArray(partyData)) {
            partyData.forEach((party) => {
              addParty(party);
            });
          } else {
            addParty(partyData);
          }

          // Update errors
          setPartyErrors((prev) => ({
            ...prev,
            ...errors,
          }));
          // The validation will happen automatically through useEffect when parties change
        }}
      />
    </div>
  );
};

export default StepFour;
